/**
 * Deep usage notes for the FUNCTIONAL fields : the ones whose effect on the site
 * is invisible from the form itself (tags, taxonomies, catégories, marqueurs
 * éditoriaux…). The short field `description` stays the one-line hint under the
 * input; these notes feed the ⓘ hover tooltip and explain exactly what the
 * system does with the value.
 *
 * Keyed by NORMALIZED field key (see normalizeFieldKeyLookup) so one note is
 * reused by every content type that declares the same field : « À la une » on
 * l'article s'explique une fois, pas 69 fois.
 */
export const FIELD_USAGE_NOTES: Record<string, string> = {
	tags:
		"Les tags sont enregistrés comme une liste séparée par des virgules. Ils sont affichés sur la page du contenu " +
		"et alimentent l'index de recherche du site : un visiteur qui filtre ou recherche par mot-clé retrouve le contenu " +
		"via ses tags. Ils sont partagés entre tous les contenus du même type : réutilisez un tag existant (les suggestions " +
		"pendant la saisie) plutôt que d'en créer une variante (« BCC » et « B.C.C. » deviendraient deux filtres distincts).",
	taxonomyids:
		"Les taxonomies classent le contenu de façon transversale. Elles alimentent les filtres des pages de liste du site " +
		"public (les visiteurs peuvent restreindre une liste aux taxonomies choisies) ainsi que les filtres du navigateur " +
		"de contenu de cette administration. Un contenu sans taxonomie reste visible partout : il n'est simplement jamais " +
		"remonté par ces filtres.",
	categorypathids:
		"La catégorie détermine l'emplacement de l'article sur le site : elle fixe le chemin de son URL " +
		"(/articles/<sujet>/<titre>), son fil d'Ariane, et la page de rubrique dans laquelle il est listé. " +
		"Changer la catégorie après publication déplace l'URL de l'article : l'ancienne adresse est alors redirigée.",
	"is-featured":
		"Marqueur éditorial « À la une » : il désigne l'article que la rédaction met en tête des blocs d'actualités " +
		"(la grande carte principale). Un seul article devrait porter ce marqueur à la fois : pensez à le retirer de " +
		"l'article précédent quand vous le posez sur un nouveau.",
	"is-secondary-featured":
		"Marqueur éditorial « En avant » : il désigne les articles mis en valeur dans le bloc secondaire des pages " +
		"d'actualités, juste sous l'article à la une. Réservez-le à une poignée d'articles récents : le bloc n'affiche " +
		"que les premiers.",
	date:
		"La date de référence du contenu. Elle commande le tri chronologique des listes et des blocs « récents » du site, " +
		"est affichée sur la page publique, et : pour les types dont l'adresse contient la date : construit l'URL de la page. " +
		"Le format saisi est normalisé automatiquement à l'enregistrement.",
	title:
		"Le titre identifie le contenu partout : en-tête de la page publique, cartes des listes, résultats de recherche. " +
		"Pour les types dont l'adresse se construit sur le titre (les vidéos par exemple), le modifier change l'URL de la page.",
	slug:
		"Identifiant d'adresse : il fixe le segment d'URL de la page (…/<slug>) et sert de clé stable pour les références " +
		"entre contenus. Le changer déplace la page vers une nouvelle adresse : à éviter après publication.",
	pdf:
		"Le document PDF du contenu : il est prévisualisé sur la page publique, proposé au téléchargement, et son texte " +
		"est indexé : un visiteur retrouve ce contenu en recherchant des mots présents dans le PDF.",
	body:
		"Le corps de la page, composé de blocs de contenu. Il est rendu tel quel sur la page publique et son texte " +
		"alimente la recherche du site.",
	components:
		"Le corps de la page, composé de blocs de contenu. Il est rendu tel quel sur la page publique et son texte " +
		"alimente la recherche du site.",
	image:
		"Illustration du contenu : elle sert de miniature sur les cartes des listes et d'image sur la page. " +
		"Choisissez-la dans la médiathèque : elle est optimisée automatiquement pour le site.",
	embedurl:
		"Lien d'intégration de la vidéo (https://www.youtube.com/embed/…). Le site l'affiche comme lecteur intégré 16:9 " +
		"sur la page de la vidéo ; une URL YouTube classique est normalisée automatiquement.",
	valeur:
		"La valeur numérique de l'enregistrement (taux, montant, indice). Elle est affichée telle quelle sur la page " +
		"et dans les tableaux de la série : saisissez uniquement le nombre, sans unité.",
	indicateur:
		"Le libellé de l'indicateur mesuré. Il regroupe les enregistrements d'une même série : gardez exactement la même " +
		"orthographe d'une période à l'autre, sinon la série se scinde en deux.",
	datedebut:
		"Début de la période couverte. Sert à l'ordre chronologique (notamment quand le contenu n'a pas de date simple) " +
		"et à l'affichage de la période sur la page.",
	datefin:
		"Fin de la période couverte, affichée avec la date de début pour situer l'enregistrement dans le temps.",
	path:
		"Adresse de la page, générée automatiquement à partir des données (selon le modèle du type : date, titre ou slug). " +
		"Elle se recalcule à chaque enregistrement. Le bouton « Remplacer le chemin » permet d'en fixer une à la main : " +
		"elle est alors conservée telle quelle jusqu'à « Rétablir le chemin automatique ». Si l'adresse change, " +
		"l'ancienne doit être redirigée et la page redéployée.",
};

const normalizeKey = (value?: string | null) =>
	(value ?? "")
		.trim()
		.replace(/[^a-zA-Z0-9]+/g, "-")
		.replace(/^-+|-+$/g, "")
		.toLowerCase();

/** Usage note for a field key/title, or null when the field has no registered note. */
export const getFieldUsageNote = (keyOrTitle?: string | null): string | null => {
	const normalized = normalizeKey(keyOrTitle);
	if (!normalized) return null;
	// The map's keys are already normalized-lowercase; camelCase keys (taxonomyIds)
	// normalize to "taxonomyids", kebab keys (is-featured) stay as-is.
	return FIELD_USAGE_NOTES[normalized] ?? null;
};
